import { FluentBundle } from 'fluent';
import { GetString } from 'fluent-react';
import React, {useState} from 'react';
import styled from 'styled-components';
import Localized from '../landing/Localized';
import { primaryColor1, secondaryColor3 } from '../Utils';
import {
  PolicyDimension,
} from '../Utils';

const RadioLabel = styled.label`
  display: block;
  margin-bottom: 9px;
  font-size: 0.875rem;
  color: ${primaryColor1};
  position: relative;
  width: 65%;

  &:first-of-type {
    margin-top: 11px;
  }

  &:hover {
    cursor: pointer;
  }

  @media screen and (max-width: 1200px), screen and (max-height: 700px) {
    font-size: 0.75rem;
    margin-bottom: 4px;
    width: 85%;

    &:first-of-type {
      margin-top: 6px;
    }
  }
`;

const Span = styled.span`
  border-bottom-width: 3px;
  border-bottom-style: solid;

  &::after {
    content: '';
    display: inline-block;
    height: 16px;
    width: 16px;
    border: 1px solid ${primaryColor1};
    border-radius: 100%;
    position: absolute;
    right: 0;
    top: 2px;
  }

  &::before {
    content: '';
    height: 10px;
    width: 10px;
    background-color: var(--inner-circle-color);
    border-radius: 100%;
    position: absolute;
    top: 5px;
    right: 3px;
  }

  &:hover {
    opacity: 0.7;
  }

  @media screen and (max-width: 1200px), screen and (max-height: 700px) {
    &::after {
      height: 11px;
      width: 11px;
    }

    &::before {
      height: 5px;
      width: 5px;
    }
  }
`;

const RadioButton = styled.input`
  display: none;
`;

const arrowSize = 8;
const arrowBorder = 1;

const Tooltip = styled.div`
  position: absolute;
  background-color: white;
  border: 1px solid ${primaryColor1};
  font-size: 0.8rem;
  top: -0.5rem;
  right: 105%;
  width: 12.5rem;
  padding: 7px;
  line-height: 1.5;

  &::after,
  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    top: 9px;
    border-top: ${arrowSize}px solid transparent;
    border-bottom: ${arrowSize}px solid transparent;
  }

  &::before {
    right: -${arrowSize + arrowBorder}px;
    border-left: ${arrowSize}px solid ${primaryColor1};
  }

  &::after {
    right: -${arrowSize}px;
    border-left: ${arrowSize}px solid white;
  }
`;

const DimensionName = styled.strong`
  display: block;
`;

// Allow CSS custom properties
declare module 'csstype' {
  interface Properties {
    '--inner-circle-color'?: string;
  }
}

interface Props {
  selectedValue: PolicyDimension;
  elmValue: PolicyDimension;
  setValue: (value: PolicyDimension) => void;
  labelTextArg: string;
  tooltipTextArg: string;
  getFluentString: GetString;
  fluentBundle: FluentBundle;
  emphasizedTextArg: string;
}

const PolicyDimensionRadioButton = (props: Props) => {
  const {
    selectedValue, elmValue, setValue, tooltipTextArg,
    getFluentString, fluentBundle, labelTextArg, emphasizedTextArg,
  } = props;
  const labelText = getFluentString(labelTextArg);

  const [isTooltipShown, setIsTooltipShown] = useState<boolean>(false);

  const tooltipArgs = {
    dimensionName: (
      <DimensionName
        children={`${labelText}:`}
      />
    ),
    emphasizedText: <em>{emphasizedTextArg}</em>,
  };

  let tooltip: React.ReactElement<any> | null;
  if (isTooltipShown === true) {
    tooltip = (
      <Localized
        bundle={fluentBundle}
        messageId={tooltipTextArg}
        messageArgs={tooltipArgs}
      >
        <Tooltip />
      </Localized>
    );
  } else {
    tooltip = null;
  }

  let spanStyle: React.CSSProperties;
  if (selectedValue === elmValue) {
    spanStyle = {
      '--inner-circle-color': primaryColor1,
      'borderBottomColor': secondaryColor3,
    };
  } else {
    spanStyle = {
      '--inner-circle-color': 'transparent',
      'borderBottomColor': 'transparent',
    };
  }

  const onMouseEnter = () => setIsTooltipShown(true);
  const onMouseLeave = () => setIsTooltipShown(false);

  return (
    <RadioLabel
        htmlFor={elmValue.toString()}
        key={elmValue}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Span style={spanStyle}>
            {labelText}
        </Span>
        <RadioButton
          type='radio'
          id={elmValue.toString()}
          name='policyDimensionRadioGroup'
          onChange={() => setValue(elmValue)}
          checked={selectedValue === elmValue}
        />
      {tooltip}
    </RadioLabel>
  );
};

export default PolicyDimensionRadioButton;
